import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, TextField, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import * as Auth from "fond/cognito/auth";
import { INVITATION_ID_KEY, RETURN_URL } from "fond/constants";
import { CognitoUser, MFA_SOFTWARE_TOKEN } from "fond/types";
import { CognitoResponseCode } from "fond/utils/auth";
import { useQueryParams } from "fond/utils/hooks";
import { SystemErrorDialog } from "fond/widgets";

import { Paper } from "./newPasswordForm.styles";

interface IProps {
  cognitoUser: CognitoUser;
}

const MFAChallengeForm: React.FC<IProps> = ({ cognitoUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cognitoResponseCode, setCognitoResponseCode] = useState<string | null>(null);
  const returnUrl = useQueryParams(RETURN_URL) || "/";
  const invitationId = useQueryParams(INVITATION_ID_KEY) || location.state?.invitationId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError("Please enter a 6-digit code.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await Auth.confirmSignIn(cognitoUser, code, MFA_SOFTWARE_TOKEN);
      analytics.identify(cognitoUser.getUsername());
      analytics.track("Signed in", { source: "signin-mfa" });
      analytics.setProfile({ $email: cognitoUser.getUsername() });
      navigate(returnUrl, { state: { ...location.state, invitationId } });
    } catch (err: unknown) {
      setLoading(false);
      const errCode = err && typeof err === "object" && "code" in err ? (err as { code: string }).code : "";
      if (errCode === CognitoResponseCode.NETWORK_ERROR || errCode === CognitoResponseCode.UNKNOWN_ERROR) {
        setCognitoResponseCode(errCode);
      } else {
        setError("Invalid verification code. Please try again.");
      }
    }
  };

  return (
    <>
      <SystemErrorDialog
        open={cognitoResponseCode === CognitoResponseCode.NETWORK_ERROR || cognitoResponseCode === CognitoResponseCode.UNKNOWN_ERROR}
        networkError={cognitoResponseCode === CognitoResponseCode.NETWORK_ERROR}
        onClose={() => setCognitoResponseCode(null)}
      />
      <Paper elevation={2}>
        <form onSubmit={handleSubmit}>
          <Typography align="left" sx={{ mb: 1 }}>
            Enter the verification code from your authenticator app.
          </Typography>
          <Box mt={2}>
            <TextField
              autoFocus
              fullWidth
              label="Verification code"
              value={code}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                setCode(val);
                setError(null);
              }}
              inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              placeholder="000000"
            />
          </Box>
          {error && (
            <Box mt={2}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          <Box mt={2}>
            <LoadingButton disabled={code.length !== 6} loading={loading} type="submit" size="large" fullWidth variant="contained" color="primary">
              Verify
            </LoadingButton>
          </Box>
        </form>
      </Paper>
    </>
  );
};

export default MFAChallengeForm;
